import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import LearnLanguageNav from './LearnLanguageNav';
import {
  ARTICLES, ARTICLE_BY_SLUG, CATEGORY_INFO, FEATURED_SLUG, ORIGIN,
  homePath, learnHubPath, meaningsHubPath, noteLearnClick,
  pageDir, readyLearnLangs, sampleCards, useLearnBundle,
} from './learnData';
import './LearnTarot.css';

const ENGLISH = {
  title: 'Learn Tarot — Beginner Guides, Card Meanings & Tarot History | NowTarot',
  description: 'New to tarot? Explore free, easy-to-follow guides covering card meanings, tarot spreads, symbolism, history, and how to read your own cards. Learn at your own pace, then practice with NowTarot.',
  kicker: 'Free tarot guides',
  h1: 'Learn Tarot, One Card at a Time',
  intro: 'New to tarot? Explore free, easy-to-follow guides covering card meanings, tarot spreads, symbolism, history, and how to read your own cards. Learn at your own pace, then practice with NowTarot.',
  startHere: 'Start here',
  readGuide: 'Read the beginner guide',
  browse: 'Browse by topic',
  allGuides: 'All guides',
  minRead: '{n} min read',
  meaningsTitle: 'Explore all 78 tarot card meanings',
  meaningsBody: 'Every card in the Rider–Waite–Smith deck, upright and reversed, with the symbols to look for and how each card tends to read in love, work and day-to-day questions.',
  meaningsButton: 'Browse card meanings',
  sampleLabel: 'Sample cards',
  practiceTitle: 'Practice what you learned',
  practice: [
    { to: '/free-tarot/', label: 'Free Tarot Reading', text: 'Shuffle a virtual deck and draw your own cards.' },
    { to: '/three-card-tarot/', label: 'Three-Card Tarot', text: 'Past, present and future in one small spread.' },
    { to: '/card-of-the-day/', label: 'Card of the Day', text: 'One card, one question to reflect on today.' },
    { to: '/tarot-card-interpreter/', label: 'Interpret Your Own Cards', text: 'Enter cards you drew from a physical deck and ask how they relate.' },
    { to: '/yes-or-no-tarot/', label: 'Yes or No Tarot', text: 'A quick one-card answer for simple questions.' },
    { to: '/celtic-cross-tarot/', label: 'Celtic Cross', text: 'The classic ten-card spread for a fuller picture.' },
  ],
  missionTitle: 'Why we made this library',
  mission: 'NowTarot exists to make tarot, astrology and personal reflection free to explore. These guides are here so you can learn the craft without paying for a course. Each one is built from published sources: museum collections, historians, and experienced tarot teachers. We check every factual claim against those sources and list them at the end of the guide. Where readers disagree, we say so. Tarot is presented here as a tradition and a tool for reflection, not as a way to know the future for certain.',
  categories: {
    'getting-started': { label: 'Getting Started', blurb: 'Your first reading, how the deck works, and how to ask questions that lead somewhere useful.' },
    spreads: { label: 'Tarot Spreads', blurb: 'Choose a layout, understand what each position does, and read several cards as one story.' },
    interpretation: { label: 'Interpretation & Symbolism', blurb: 'Reversals, the Major and Minor Arcana, and using tarot as a tool for reflection.' },
    history: { label: 'History & Divination', blurb: 'Where tarot actually came from, and how it fits among other divination traditions.' },
  },
  ui: { home: 'Home', learn: 'Learn Tarot', breadcrumb: 'Breadcrumb' },
};

function alternateLinks() {
  const langs = readyLearnLangs();
  if (langs.length < 2) return null;
  return [
    <link key="x-default" rel="alternate" hrefLang="x-default" href={`${ORIGIN}${learnHubPath('en')}`} />,
    ...langs.map((item) => (
      <link key={item.code} rel="alternate" hrefLang={item.code} href={`${ORIGIN}${learnHubPath(item.code)}`} />
    )),
  ];
}

const LearnHub = ({ lang = 'en' }) => {
  const bundle = useLearnBundle(lang);
  if (lang !== 'en' && bundle === undefined) {
    return (
      <div className="learn-page" lang={lang} dir={pageDir(lang)}>
        <Header />
        <main className="lt-container"><p className="lt-loading">…</p></main>
        <Footer />
      </div>
    );
  }
  if (lang !== 'en' && !bundle) return <Navigate to={learnHubPath('en')} replace />;

  const copy = lang === 'en' ? ENGLISH : bundle.hub;
  const articles = lang === 'en'
    ? ARTICLES
    : ARTICLES.map((item) => bundle.articles[item.slug]).filter(Boolean);
  const featured = lang === 'en' ? ARTICLE_BY_SLUG[FEATURED_SLUG] : bundle.articles[FEATURED_SLUG];
  const categories = CATEGORY_INFO
    .map((cat) => ({
      ...cat,
      label: copy.categories[cat.id].label,
      blurb: copy.categories[cat.id].blurb,
      articles: articles.filter((article) => article.category === cat.id),
    }))
    .filter((cat) => cat.articles.length > 0);
  const canonical = `${ORIGIN}${learnHubPath(lang)}`;
  const home = homePath(lang);
  const dir = pageDir(lang);
  const cards = sampleCards(lang);
  const meanings = meaningsHubPath(lang);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: copy.ui.learn,
      description: copy.description,
      url: canonical,
      inLanguage: lang,
      hasPart: articles.map((article) => ({
        '@type': 'Article',
        name: article.title,
        url: `${ORIGIN}${article.path}`,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: copy.ui.home, item: `${ORIGIN}${home}` },
        { '@type': 'ListItem', position: 2, name: copy.ui.learn, item: canonical },
      ],
    },
  ];

  return (
    <div className="learn-page" lang={lang} dir={dir}>
      <Helmet htmlAttributes={{ lang, dir }}>
        <title>{copy.title}</title>
        <meta name="description" content={copy.description} />
        <link rel="canonical" href={canonical} />
        {alternateLinks()}
        <meta property="og:title" content={copy.title} />
        <meta property="og:description" content={copy.description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${ORIGIN}/og-image.png`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />

      <main className="lt-container">
        <nav className="lt-breadcrumbs" aria-label={copy.ui.breadcrumb}>
          <Link to={home}>{copy.ui.home}</Link>
          <span aria-hidden="true"> › </span>
          <span>{copy.ui.learn}</span>
        </nav>

        <header className="lt-hub-hero">
          <p className="lt-kicker">{copy.kicker}</p>
          <h1>{copy.h1}</h1>
          <p>{copy.intro}</p>
        </header>

        {featured && (
          <section className="lt-featured" aria-labelledby="lt-featured-title">
            {featured.featured?.src && (
              <Link
                to={featured.path}
                className="lt-featured-art"
                tabIndex={-1}
                aria-hidden="true"
                onClick={() => noteLearnClick('learn_article_click', featured.path)}
              >
                <img src={featured.featured.src} alt="" width="1200" height="630" />
              </Link>
            )}
            <div className="lt-featured-text">
              <p className="lt-kicker">{copy.startHere}</p>
              <h2 id="lt-featured-title">
                <Link to={featured.path} onClick={() => noteLearnClick('learn_article_click', featured.path)}>
                  {featured.title}
                </Link>
              </h2>
              <p>{featured.dek || featured.metaDescription}</p>
              <Link
                to={featured.path}
                className="lt-button"
                onClick={() => noteLearnClick('learn_article_click', featured.path)}
              >
                {copy.readGuide} <span className="lt-arrow" aria-hidden="true">→</span>
              </Link>
            </div>
          </section>
        )}

        {categories.length > 0 && (
          <section className="lt-section" aria-labelledby="lt-categories">
            <h2 id="lt-categories">{copy.browse}</h2>
            <div className="lt-category-grid">
              {categories.map((cat) => (
                <div className={`lt-category lt-accent-${cat.accent}`} key={cat.id}>
                  <h3>{cat.label}</h3>
                  <p>{cat.blurb}</p>
                  <ul>
                    {cat.articles.map((article) => (
                      <li key={article.slug}>
                        <Link to={article.path} onClick={() => noteLearnClick('learn_article_click', article.path)}>
                          {article.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}

        {articles.length > 0 && (
          <section className="lt-section" aria-labelledby="lt-all-guides">
            <h2 id="lt-all-guides">{copy.allGuides}</h2>
            <div className="lt-article-grid">
              {articles.map((article) => (
                <Link
                  to={article.path}
                  className="lt-article-card"
                  key={article.slug}
                  onClick={() => noteLearnClick('learn_article_click', article.path)}
                >
                  {article.featured?.src && <img src={article.featured.src} alt="" width="1200" height="630" loading="lazy" />}
                  <span className="lt-article-card-body">
                    <span className="lt-chip">{article.categoryLabel}</span>
                    <span className="lt-article-card-title">{article.title}</span>
                    <span className="lt-article-card-meta">{copy.minRead.replace('{n}', article.readingMinutes)}</span>
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="lt-section lt-meanings-band" aria-labelledby="lt-meanings">
          <div>
            <h2 id="lt-meanings">{copy.meaningsTitle}</h2>
            <p>{copy.meaningsBody}</p>
            <Link to={meanings} className="lt-button" onClick={() => noteLearnClick('learn_tool_click', meanings)}>
              {copy.meaningsButton} <span className="lt-arrow" aria-hidden="true">→</span>
            </Link>
          </div>
          <div className="lt-meanings-cards" aria-label={copy.sampleLabel}>
            {cards.map((card) => (
              <Link to={card.to} key={card.slug} title={card.name}>
                <img src={card.image} alt={card.name} width="90" height="158" loading="lazy" />
              </Link>
            ))}
          </div>
        </section>

        <section className="lt-section" aria-labelledby="lt-practice">
          <h2 id="lt-practice">{copy.practiceTitle}</h2>
          <div className="lt-practice-grid">
            {copy.practice.map((tool) => (
              <Link
                to={tool.to}
                className="lt-practice-card"
                key={tool.to}
                onClick={() => noteLearnClick('learn_tool_click', tool.to)}
              >
                <strong>{tool.label}</strong>
                <span>{tool.text}</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="lt-section lt-mission" aria-labelledby="lt-mission">
          <h2 id="lt-mission">{copy.missionTitle}</h2>
          <p>{copy.mission}</p>
        </section>

        <LearnLanguageNav lang={lang} />
      </main>

      <Footer />
    </div>
  );
};

export default LearnHub;
